import axiosInstance from "../utils/index";
import axios from "axios";
import { Hospital } from "../app/currentHospital";
import {
  readAssistantSessionCache,
  writeAssistantSessionCache,
} from "../utils/hospitalSwitcherView";

export type HierarchyNode = {
  hospitalId: string;
  parentId?: string | null;
  name: string;
  isDefault?: boolean;
  slave?: boolean;
  hospitalImage?: string;
  hospitalBackground?: string;
  /** True when listed in hospital_master; false for platform accounts not yet linked. */
  inHierarchy?: boolean;
  /** root | brand | site when this node belongs to Atom / Jones / I-MED. */
  clinicNetworkRole?: "root" | "brand" | "site" | string;
  email?: string;
  children?: HierarchyNode[];
};

export type NetworkRole = {
  grandmaster: boolean;
  masterRoot: boolean;
  canManageOrg: boolean;
  effectiveMasterId: string;
  clinicNetwork?: boolean;
  experience?: string;
};

const HOSPITAL_MASTER_BASE = "/v1/admin/hospital-master";

function getAuthToken(): string {
  const masterToken = localStorage.getItem("master-token");
  return masterToken || localStorage.getItem("token-name") || "";
}

function getApiBaseUrl(): string {
  return axiosInstance.defaults.baseURL || process.env.REACT_APP_URL || "";
}

function buildHospitalMasterUrl(path: string): string {
  const baseURL = getApiBaseUrl();
  return baseURL ? `${baseURL}${HOSPITAL_MASTER_BASE}${path}` : `${HOSPITAL_MASTER_BASE}${path}`;
}

async function hospitalMasterRequest<T>(
  method: "get" | "post" | "delete",
  path: string,
  body?: unknown,
): Promise<T | null> {
  const url = buildHospitalMasterUrl(path);
  const headers = {
    "doctor-admin-token": getAuthToken(),
    "Accept-Language": "en-US",
  };

  try {
    const res =
      method === "get"
        ? await axios.get(url, { headers })
        : method === "post"
          ? await axios.post(url, body, { headers })
          : await axios.delete(url, { headers });

    if (res?.data?.code === 200) {
      return res.data.data as T;
    }
    throw new Error(res?.data?.message || "Request failed");
  } catch (error: any) {
    const message =
      error?.response?.data?.message || error?.message || "Request failed";
    throw new Error(message);
  }
}

export async function getHierarchyTree(
  forceRefresh = false,
): Promise<HierarchyNode | null> {
  await getGrandmasterHierarchy(forceRefresh);
  return hospitalMasterRequest<HierarchyNode>("get", "/grandmaster-hierarchy");
}

export async function getNetworkRole(): Promise<NetworkRole | null> {
  return hospitalMasterRequest<NetworkRole>("get", "/network-role");
}

export async function assignHospitalParent(
  hospitalId: string,
  parentId: string,
): Promise<void> {
  await hospitalMasterRequest("post", "/assign-parent", { hospitalId, parentId });
}

export async function promoteHospitalToMaster(hospitalId: string): Promise<void> {
  await hospitalMasterRequest("post", "/promote-to-master", { hospitalId });
}

export async function unlinkHospitalFromHierarchy(
  hospitalId: string,
): Promise<void> {
  await hospitalMasterRequest("delete", `/unlink/${hospitalId}`);
}

export function flattenHierarchyNodes(
  node: HierarchyNode | null | undefined,
  acc: HierarchyNode[] = [],
): HierarchyNode[] {
  if (!node?.hospitalId) return acc;
  acc.push(node);
  (node.children || []).forEach((child) => flattenHierarchyNodes(child, acc));
  return acc;
}

/** Master root under the grandmaster — in hospital_master or already has members. */
export function isMasterRoot(
  node: HierarchyNode,
  depth: number,
  knownMasterIds?: Set<string>,
): boolean {
  if (depth !== 1) return false;
  if ((node.children?.length || 0) > 0) return true;
  if (knownMasterIds?.has(node.hospitalId)) return true;
  return (
    node.inHierarchy === true &&
    (node.parentId == null || node.parentId === "")
  );
}

/** All master accounts under the grandmaster that can receive hospitals. */
export function collectMasterRootsForAssign(
  root: HierarchyNode | null,
  grandmasterId?: string,
  knownMasterIds?: Set<string>,
): HierarchyNode[] {
  if (!root?.children?.length) return [];
  return root.children.filter(
    (n) =>
      n.hospitalId !== grandmasterId &&
      n.hospitalId !== root.hospitalId &&
      isMasterRoot(n, 1, knownMasterIds),
  );
}

/** Direct children of the grandmaster that are not masters — can be assigned under one. */
export function collectHospitalsAssignableUnderMaster(
  root: HierarchyNode | null,
  grandmasterId?: string,
  knownMasterIds?: Set<string>,
): HierarchyNode[] {
  if (!root?.children?.length) return [];
  return root.children.filter(
    (n) =>
      n.hospitalId !== grandmasterId &&
      !isMasterRoot(n, 1, knownMasterIds),
  );
}

/** @deprecated use isMasterRoot */
export function isDisplayMaster(node: HierarchyNode): boolean {
  return (node.children?.length || 0) > 0;
}

/** @deprecated use isMasterRoot */
export function isPromotedGroupLeader(
  node: HierarchyNode,
  depth: number,
): boolean {
  return isMasterRoot(node, depth);
}

/** @deprecated use collectMasterRootsForAssign */
export function collectDisplayMastersForAssign(
  root: HierarchyNode | null,
  grandmasterId?: string,
): HierarchyNode[] {
  return collectMasterRootsForAssign(root, grandmasterId);
}

/** @deprecated use collectHospitalsAssignableUnderMaster */
export function collectDirectLeafChildren(
  root: HierarchyNode | null,
  excludeHospitalId?: string,
): HierarchyNode[] {
  const list = collectHospitalsAssignableUnderMaster(root);
  if (!excludeHospitalId) return list;
  return list.filter((n) => n.hospitalId !== excludeHospitalId);
}

export function collectParentCandidates(
  root: HierarchyNode | null,
  excludeHospitalId?: string,
): HierarchyNode[] {
  if (!root) return [];
  const all = flattenHierarchyNodes(root);
  const excluded = new Set<string>();
  if (excludeHospitalId) {
    const node = all.find((n) => n.hospitalId === excludeHospitalId);
    if (node) {
      flattenHierarchyNodes(node).forEach((n) => excluded.add(n.hospitalId));
    }
  }
  return all.filter((n) => !excluded.has(n.hospitalId));
}

export function collectMasterAccountsForAssign(
  root: HierarchyNode | null,
  excludeHospitalId?: string,
): HierarchyNode[] {
  if (!root) return [];

  const all = flattenHierarchyNodes(root);
  const excluded = new Set<string>();
  if (excludeHospitalId) {
    const node = all.find((n) => n.hospitalId === excludeHospitalId);
    if (node) {
      flattenHierarchyNodes(node).forEach((n) => excluded.add(n.hospitalId));
    }
  }

  return all.filter((n) => {
    if (excluded.has(n.hospitalId)) return false;
    if (n.hospitalId === root.hospitalId) return false;
    if (n.inHierarchy !== true) return false;
    const childCount = n.children?.length || 0;
    return childCount > 0 || n.parentId == null || n.parentId === "";
  });
}

/** @deprecated use collectMasterAccountsForAssign */
export function collectMasterCandidates(
  root: HierarchyNode | null,
  excludeHospitalId?: string,
): HierarchyNode[] {
  if (!root) return [];
  const all = flattenHierarchyNodes(root);
  const excluded = new Set<string>();
  if (excludeHospitalId) {
    const node = all.find((n) => n.hospitalId === excludeHospitalId);
    if (node) {
      flattenHierarchyNodes(node).forEach((n) => excluded.add(n.hospitalId));
    }
  }
  return all.filter(
    (n) =>
      !excluded.has(n.hospitalId) &&
      (!n.parentId || n.parentId === null || n.parentId === ""),
  );
}

type ParentWithChildrenResponse = {
  code: number;
  data?: {
    hospitalId: string;
    parentId: string | null;
    name: string;
    isDefault: boolean;
    slave?: boolean;
    hospitalImage?: string;
    firebaseToken?: string;
    hospitalBackground?: string;
    children: Array<{
      hospitalId: string;
      name: string;
      children?: any[];
      slave?: boolean;
      hospitalImage?: string;
      firebaseToken?: string;
    }>;
  };
  message?: string;
};

// Store parent hospital info in localStorage for persistent access across sessions
const PARENT_HOSPITAL_KEY = "persistent-parent-hospital";
const PARENT_CHILDREN_KEY = "persistent-parent-children-v3";
const HOSPITAL_LIST_TIMESTAMP_KEY = "hospital-list-timestamp-v3";
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes — signed hospital logos expire

export async function getGrandmasterHierarchy(
  forceRefresh: boolean = false,
): Promise<Hospital[]> {
  //console.log(
  // "🏥 API: Fetching grandmaster hierarchy, forceRefresh:",
  //  forceRefresh,
  // );

  // Check if we should use cached data (unless forced refresh)
  if (!forceRefresh) {
    try {
      const storedParentInfo = localStorage.getItem(PARENT_HOSPITAL_KEY);
      const storedChildrenInfo = localStorage.getItem(PARENT_CHILDREN_KEY);
      const timestampStr = localStorage.getItem(HOSPITAL_LIST_TIMESTAMP_KEY);

      if (storedParentInfo && storedChildrenInfo && timestampStr) {
        const timestamp = parseInt(timestampStr);
        const isWithinCacheWindow = Date.now() - timestamp < CACHE_DURATION;

        if (isWithinCacheWindow) {
          const parentHospital = JSON.parse(storedParentInfo);
          const children = JSON.parse(storedChildrenInfo);
          console.log(
            "🏥 API: Using cached hierarchy from localStorage:",
            parentHospital,
          );
          return [parentHospital, ...children];
        } else {
          console.log("🏥 API: Cache expired, fetching fresh data");
        }
      }
    } catch (error) {
      console.log("🏥 API: Error reading cached info:", error);
    }
  }

  // Fetch from new endpoint using CLEAN axios request to avoid X-Hospital-Id context pollution
  // This ensures we get the true account hierarchy, not a slave's view
  const masterToken = localStorage.getItem("master-token");
  const token = masterToken || localStorage.getItem("token-name");

  // Robustly determine baseURL (never emit //path from trailing-slash base + absolute path)
  const baseURL = (
    axiosInstance.defaults.baseURL ||
    process.env.REACT_APP_URL ||
    ""
  ).replace(/\/+$/, "");
  // User confirmed endpoint: /v1/admin/hospital-master/grandmaster-hierarchy
  const endpoint = "/v1/admin/hospital-master/grandmaster-hierarchy";
  const url = baseURL ? `${baseURL}${endpoint}` : endpoint;

  // console.log("🏥 API: Making clean request to:", url);

  try {
    const res = await axios.get<ParentWithChildrenResponse>(url, {
      headers: {
        "doctor-admin-token": token || "",
        "Accept-Language": "en-US",
        // Explicitly remove X-Hospital-Id to force root context
        // Note: axios might not send it anyway since this is a fresh request,
        // but we ensure it's not inherited from anywhere
      },
    });
    const body = res.data as ParentWithChildrenResponse;

    if (body.code !== 200 || !body.data) {
      console.log(
        "🏥 API: No data returned from API or non-200 code:",
        body.code,
      );
      return [];
    }

    const flattenHospitals = (node: any, map: Map<string, Hospital>, parentId?: string | null) => {
      if (!node || !node.hospitalId) return;
      if (!map.has(node.hospitalId)) {
        map.set(node.hospitalId, {
          id: node.hospitalId,
          name: node.name,
          slave: node.slave,
          parentId: parentId ?? node.parentId ?? null,
          hospitalImage: node.hospitalImage,
          hospitalBackground: node.hospitalBackground,
          firebaseToken: node.firebaseToken,
          clinicNetworkRole: node.clinicNetworkRole,
          inHierarchy: node.inHierarchy,
        });
      }
      if (Array.isArray(node.children)) {
        node.children.forEach((child: any) =>
          flattenHospitals(child, map, node.hospitalId),
        );
      }
    };

    const hospitalMap = new Map<string, Hospital>();

    // Seed root hospital
    flattenHospitals(body.data, hospitalMap, null);

    const currentHospital: Hospital = hospitalMap.get(body.data.hospitalId)!;
    hospitalMap.delete(body.data.hospitalId);

    const children: Hospital[] = Array.from(hospitalMap.values());

    // Fetch assistants and add them
    /*   try {
      const assistants = await getHospitalAssistant();
      if (Array.isArray(assistants) && assistants.length > 0) {
        assistants.forEach((a: any) => {
          children.push({
            id: a.hospitalId || a.accountId || a.id,
            name: a.name || a.hospitalName,
            slave: a.slave ?? false,
            hospitalImage: a.hospitalImage || a.image,
            firebaseToken: a.firebaseToken,
          });
        });
      }
    } catch (e) {
      console.warn("API: Error fetching assistants:", e);
    }*/

    //   console.log("🏥 API: Current hospital:", currentHospital);
    //console.log("🏥 API: Children count:", children.length);

    // Check if it's a Master or Grandmaster (has children)
    if (children.length > 0) {
      localStorage.setItem(
        PARENT_HOSPITAL_KEY,
        JSON.stringify(currentHospital),
      );
      localStorage.setItem(PARENT_CHILDREN_KEY, JSON.stringify(children));
      localStorage.setItem(HOSPITAL_LIST_TIMESTAMP_KEY, Date.now().toString());
      return [currentHospital, ...children];
    }

    try {
      const storedParentInfo = localStorage.getItem(PARENT_HOSPITAL_KEY);
      const storedChildrenInfo = localStorage.getItem(PARENT_CHILDREN_KEY);
      if (storedParentInfo && storedChildrenInfo) {
        const parentHospital = JSON.parse(storedParentInfo);
        const cachedChildren = JSON.parse(storedChildrenInfo);
        if (Array.isArray(cachedChildren) && cachedChildren.length > 0) {
          return [parentHospital, ...cachedChildren];
        }
      }
    } catch {
      // ignore
    }

    localStorage.setItem(PARENT_HOSPITAL_KEY, JSON.stringify(currentHospital));
    localStorage.setItem(PARENT_CHILDREN_KEY, JSON.stringify([]));
    localStorage.setItem(HOSPITAL_LIST_TIMESTAMP_KEY, Date.now().toString());

    return [currentHospital];
  } catch (error) {
    console.error("🏥 API: Error fetching grandmaster hierarchy:", error);
    return [];
  }
}

export async function getHospitalsByParentName(
  hospitalName: string,
  forceRefresh: boolean = false,
): Promise<Hospital[]> {
  // Redirect to the new grandmaster hierarchy function
  // We ignore hospitalName as the new endpoint uses the token
  return getGrandmasterHierarchy(forceRefresh);
}

// Function to clear parent hospital info from localStorage (call on logout)
export function clearParentHospitalInfo(): void {
  localStorage.removeItem(PARENT_HOSPITAL_KEY);
  localStorage.removeItem(PARENT_CHILDREN_KEY);
  localStorage.removeItem(HOSPITAL_LIST_TIMESTAMP_KEY);
  console.log("🏥 API: Cleared parent hospital info from localStorage");
}

// Function to get cached hospital list without making API call
export function getCachedHospitalList(): Hospital[] | null {
  try {
    // First try to get from Redux store cache (hospitals-list)
    const reduxCachedList = localStorage.getItem("hospitals-list");
    const reduxTimestamp = localStorage.getItem("hospitals-list-timestamp");

    if (reduxCachedList && reduxTimestamp) {
      const timestamp = parseInt(reduxTimestamp);
      const isWithinCacheWindow = Date.now() - timestamp < CACHE_DURATION;

      if (isWithinCacheWindow) {
        const hospitalsList = JSON.parse(reduxCachedList);
        console.log(
          "🏥 API: Retrieved cached hospital list from Redux store cache",
        );
        return hospitalsList;
      }
    }

    // Fallback to legacy cache format
    const storedParentInfo = localStorage.getItem(PARENT_HOSPITAL_KEY);
    const storedChildrenInfo = localStorage.getItem(PARENT_CHILDREN_KEY);
    const timestampStr = localStorage.getItem(HOSPITAL_LIST_TIMESTAMP_KEY);

    if (storedParentInfo && storedChildrenInfo && timestampStr) {
      const timestamp = parseInt(timestampStr);
      const isWithinCacheWindow = Date.now() - timestamp < CACHE_DURATION;

      if (isWithinCacheWindow) {
        const parentHospital = JSON.parse(storedParentInfo);
        const children = JSON.parse(storedChildrenInfo);
        console.log("🏥 API: Retrieved cached hospital list from legacy cache");
        return [parentHospital, ...children];
      }
    }
  } catch (error) {
    console.log("🏥 API: Error retrieving cached hospital list:", error);
  }
  return null;
}

// Function to refresh hospital cache if needed
export async function refreshHospitalCacheIfNeeded(
  hospitalName: string,
): Promise<Hospital[] | null> {
  const cachedList = getCachedHospitalList();
  if (cachedList && cachedList.length > 0) {
    console.log("🏥 API: Using existing cached hospital list");
    return cachedList;
  }

  console.log("🏥 API: Cache expired or empty, refreshing from API");
  try {
    return await getHospitalsByParentName(hospitalName, true);
  } catch (error) {
    console.error("🏥 API: Error refreshing hospital cache:", error);
    return null;
  }
}

let hospitalAssistantCache: any[] | null = readAssistantSessionCache();
let hospitalAssistantInflight: Promise<any[]> | null = null;

export function peekHospitalAssistantCache(): any[] | null {
  if (hospitalAssistantCache) return hospitalAssistantCache;
  hospitalAssistantCache = readAssistantSessionCache();
  return hospitalAssistantCache;
}

function hospitalAssistantFingerprint(data: any[]): string {
  return JSON.stringify(data);
}

export function hospitalAssistantChanged(next: any[]): boolean {
  if (!hospitalAssistantCache) return true;
  return hospitalAssistantFingerprint(hospitalAssistantCache) !==
    hospitalAssistantFingerprint(next);
}

export async function getHospitalAssistant(): Promise<any[]> {
  if (hospitalAssistantInflight) return hospitalAssistantInflight;

  const request: Promise<any[]> = (async () => {
    const masterToken = localStorage.getItem("master-token");
    const token = masterToken || localStorage.getItem("token-name");
    let accountId = "";
    try {
      const rawParent = localStorage.getItem("persistent-parent-hospital");
      if (rawParent && rawParent !== "null") {
        const parent = JSON.parse(rawParent);
        if (parent?.id) accountId = String(parent.id);
      }
    } catch {
      // ignore
    }
    if (!accountId) {
      accountId = String(localStorage.getItem("selected-hospital-id") || "").trim();
    }

    const baseURL =
      axiosInstance.defaults.baseURL || process.env.REACT_APP_URL || "";
    const endpoint = "/v2/hospital/hierarchy/assistant";
    const url = baseURL ? `${baseURL}${endpoint}` : endpoint;

    try {
      const response = await axios.get(url, {
        headers: {
          "doctor-admin-token": token || "",
          "Accept-Language": "en-US",
          ...(accountId
            ? {
                accountId,
                "X-Hospital-Id": accountId,
              }
            : {}),
        },
      });

      if (
        response.data &&
        (response.data.code === 200 || response.data.code === 0) &&
        Array.isArray(response.data.data)
      ) {
        const data = response.data.data as any[];
        hospitalAssistantCache = data;
        writeAssistantSessionCache(data);
        return data;
      }
      const fallback = hospitalAssistantCache || [];
      hospitalAssistantCache = fallback;
      return fallback;
    } catch (err) {
      console.error("API: getHospitalAssistant failed:", err);
      return hospitalAssistantCache || [];
    }
  })();

  hospitalAssistantInflight = request.finally(() => {
    hospitalAssistantInflight = null;
  });
  return hospitalAssistantInflight;
}

export async function updateChildHospitalImage(
  hospitalId: string,
  media: { hospitalImage?: string; hospitalBackground?: string },
): Promise<{ hospitalImage?: string | null; hospitalBackground?: string | null }> {
  const res = await axiosInstance.put("/v1/hospital/account/child-hospital-image", {
    hospitalId,
    ...media,
  });
  const data = res?.data?.data || {};
  return {
    hospitalImage: data.hospitalImage || data.hospital_image || null,
    hospitalBackground: data.hospitalBackground || data.hospital_background || null,
  };
}
